import { useState } from "react";
import { Share2, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { generateShareBlob } from "@/lib/shareCard";
import { buildCaption } from "@/lib/caption";

export const ShareButton = ({ result, className = "" }) => {
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const share = async () => {
    if (!result || busy) return;
    setBusy(true);
    try {
      const blob = await generateShareBlob(result);
      if (!blob) throw new Error("no blob");
      const file = new File([blob], `sybil-${result.archetype || "archetype"}.png`, {
        type: "image/png",
      });
      const caption = buildCaption(result);
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "My Sybils Archetype", text: caption });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = file.name;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        toast.success("Your archetype card was saved — share it anywhere.");
      }
    } catch (e) {
      if (!(e && e.name === "AbortError")) {
        toast.error("Could not create the image. Try again.");
      }
    } finally {
      setBusy(false);
    }
  };

  const copyCaption = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(buildCaption(result));
      setCopied(true);
      toast.success("Caption copied — paste it with your post.");
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error("Copy failed — select the caption manually.");
    }
  };

  return (
    <div className={`flex flex-wrap items-center justify-center gap-3 ${className}`}>
      <button
        onClick={share}
        disabled={!result || busy}
        className="inline-flex items-center gap-2 rounded-full border border-[#C5A059]/40 px-6 py-4 text-xs font-light uppercase tracking-[0.2em] text-[#C5A059] transition-colors duration-300 hover:bg-[#C5A059] hover:text-[#050505] disabled:opacity-40"
        data-testid="share-card-btn"
      >
        <Share2 size={15} strokeWidth={1.5} />
        {busy ? "Preparing…" : "Share as image"}
      </button>
      <button
        onClick={copyCaption}
        disabled={!result}
        className="inline-flex items-center gap-2 rounded-full px-4 py-4 text-xs font-light uppercase tracking-[0.2em] text-white/50 transition-colors duration-300 hover:text-[#C5A059] disabled:opacity-40"
        data-testid="copy-caption-btn"
      >
        {copied ? <Check size={14} strokeWidth={1.5} /> : <Copy size={14} strokeWidth={1.5} />}
        {copied ? "Copied" : "Copy caption"}
      </button>
    </div>
  );
};

export default ShareButton;
